<template>
  <div class="flex h-screen w-full overflow-hidden bg-slate-100">
    <div class="w-full flex-1 overflow-hidden">
      <Topbar />
      <div class="mt-[64px] flex h-[calc(100vh-64px)] flex-col overflow-hidden p-4 md:p-5 lg:p-6">
        <Toast />
        <ConfirmDialog></ConfirmDialog>

        <div class="mb-4 flex shrink-0 items-start justify-between gap-3">
          <div>
            <h2 class="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">Товары</h2>
            <p class="mt-1 text-sm text-slate-500 md:text-base">Список товаров, штрихкодов, классов и категорий</p>
          </div>
          <div class="flex flex-wrap items-center justify-end gap-2">
            <Button @click="newProductDialog = true" icon="pi pi-plus" label="Добавить товар" severity="success" class="!px-3 !py-2 !text-sm !shadow-sm" />
            <Button @click="openBarcodeBatchDialog()" icon="pi pi-tags" label="PDF штрихкодов" severity="secondary" class="!px-3 !py-2 !text-sm !shadow-sm" />
            <Button @click="openImportDialog()" icon="pi pi-file-import" label="Импорт Excel" severity="contrast" class="!px-3 !py-2 !text-sm !shadow-sm" />
            <Button @click="exportCSV()" icon="pi pi-upload" label="Экспорт" severity="info" class="!px-3 !py-2 !text-sm !shadow-sm" />
          </div>
        </div>

        <div class="min-h-0 flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-6">
          <DataTable
            ref="productTable"
            :value="filteredProducts"
            removableSort
            showGridlines
            stripedRows
            size="small"
            scrollable
            scrollHeight="flex"
            class="product-table h-full"
          >
            <template #header>
              <div class="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div class="flex w-full flex-wrap items-center gap-2">
                  <IconField class="w-full min-w-[16rem] flex-1 md:max-w-md">
                    <InputIcon>
                      <i class="pi pi-search" />
                    </InputIcon>
                    <InputText v-model="filters['global'].value" class="w-full" placeholder="Поиск по товару, штрихкоду или описанию..." />
                  </IconField>
                  <Select
                    v-model="selectedStorageFilter"
                    :options="storages"
                    optionLabel="name"
                    optionValue="name"
                    placeholder="Все склады"
                    showClear
                    class="w-full sm:w-52"
                  />
                  <Select
                    v-model="selectedClassificationFilter"
                    :options="classifications"
                    optionLabel="name"
                    optionValue="name"
                    placeholder="Все классы"
                    showClear
                    class="w-full sm:w-52"
                  />
                  <Select
                    v-model="selectedCategoryFilter"
                    :options="filteredCategoryOptions"
                    optionLabel="name"
                    optionValue="name"
                    placeholder="Все категории"
                    showClear
                    :disabled="!selectedClassificationFilter"
                    class="w-full sm:w-52"
                  />
                  <Button
                    type="button"
                    icon="pi pi-filter-slash"
                    label="Очистить"
                    variant="outlined"
                    severity="secondary"
                    class="w-full sm:w-auto"
                    @click="clearFilter()"
                  />
                </div>

                <div class="flex shrink-0 items-center justify-end">
                  <div class="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-slate-600">
                    Товаров <span class="ml-1 font-semibold text-emerald-700">{{ filteredProductsCount }}</span>
                  </div>
                </div>
              </div>
            </template>

            <Column field="image" header="Фото" style="width: 5%;">
              <template #body="{ data }">
                <Image class="block xl:block mx-auto rounded w-full" :src="resolveProductImage(data.image)" :alt="data.name" style="width: 48px; height: 48px;" preview/>
              </template>
            </Column>
            <Column field="name" sortable header="Название"  style="width: 16%; font-weight: 600;"></Column>
            <Column field="name_ru" sortable header="Название RU" style="width: 16%;"></Column>
            <Column field="barcode" header="Штрихкод" style="width: 20%;">
              <template #body="{ data }" style="display: flex; align-content: center; flex-wrap: wrap;">
                <Chip v-for="code in data.barcode" key="code" :label="code" class="text-[10px] py-1! m-1 self-center"/>
              </template>
            </Column>
            <Column field="storage"        sortable header="Склад"     style="width: 10%"></Column>
            <Column field="classification" sortable header="Класс"     style="width: 10%"></Column>
            <Column field="category"       sortable header="Категория" style="width: 10%"></Column>
            <!-- <Column field="description" header="Описание"  style="width: 15%"></Column> -->
            <Column class="w-120 !text-end" style="width: 12%">
              <template #body="{ data }">
                <div class="flex justify-end gap-2">
                  <Button
                    icon="pi pi-file-pdf"
                    variant="outlined"
                    severity="secondary"
                    :disabled="!getPrimaryBarcode(data)"
                    @click="exportProductBarcodePdf(data)"
                  />
                  <Button icon="pi pi-pencil" variant="outlined" severity="warn" @click="editProduct(data.id)" />
                  <Button icon="pi pi-trash" variant="outlined" severity="danger" @click="deleteProduct(data.id)" />
                </div>
              </template>
            </Column> 
          </DataTable>
        
          <Dialog
            v-model:visible="newProductDialog"
            :style="{ width: '680px', maxWidth: '96vw' }"
            header="Добавить товар"
            :modal="true"
            class="system-form-dialog"
          >
            <div class="max-h-[72vh] space-y-3 overflow-y-auto pr-1">
              <section class="rounded-2xl border border-slate-200 bg-slate-50 p-3.5">
                <div class="mb-3">
                  <h3 class="text-sm font-semibold text-slate-900">Основное</h3>
                  <p class="mt-1 text-xs text-slate-500">Название, штрихкод и описание товара.</p>
                </div>

                <div class="grid gap-3 md:grid-cols-2">
                  <div class="md:col-span-2">
                    <label for="new-name" class="mb-1 block text-sm font-medium text-slate-700">Название</label>
                    <InputText id="new-name" v-model.trim="newProduct.name" :invalid="newProductSubmitted && !newProduct.name" fluid required />
                  </div>
                  <div class="md:col-span-2">
                    <label for="new-name-ru" class="mb-1 block text-sm font-medium text-slate-700">Название RU</label>
                    <InputText id="new-name-ru" v-model.trim="newProduct.name_ru" fluid />
                  </div>
                  <div class="md:col-span-2">
                    <div class="mb-1 flex items-center justify-between gap-2">
                      <label for="new-barcode" class="block text-sm font-medium text-slate-700">Штрихкод</label>
                      <Button
                        type="button"
                        icon="pi pi-barcode"
                        label="Сгенерировать"
                        severity="secondary"
                        size="small"
                        :loading="barcodeGenerating"
                        @click="generateNewProductBarcode"
                      />
                    </div>
                    <InputText
                      id="new-barcode"
                      v-model.trim="barcode"
                      fluid
                      inputmode="numeric"
                      pattern="[0-9]*"
                      placeholder="Введите один штрихкод"
                    />
                  </div>
                  <div class="md:col-span-2">
                    <label for="new-description" class="mb-1 block text-sm font-medium text-slate-700">Описание</label>
                    <InputText id="new-description" v-model.trim="newProduct.description" fluid />
                  </div>
                </div>
              </section>

              <section class="rounded-2xl border border-slate-200 bg-white p-3.5">
                <div class="mb-3">
                  <h3 class="text-sm font-semibold text-slate-900">Классификация</h3>
                  <p class="mt-1 text-xs text-slate-500">Выберите склад, класс и категорию товара.</p>
                </div>

                <div class="grid gap-3 md:grid-cols-2">
                  <div>
                    <label for="new-classification" class="mb-1 block text-sm font-medium text-slate-700">Класс</label>
                    <Select id="new-classification" v-model="newProduct.classification_id" @change="onChangeClassification($event)" :options="classifications" :invalid="newProductSubmitted && !newProduct.classification_id" optionLabel="name" optionValue="id" fluid required />
                  </div>
                  <div>
                    <label for="new-category" class="mb-1 block text-sm font-medium text-slate-700">Категория</label>
                    <Select id="new-category" v-model="newProduct.category_id" :options="categories" :disabled="!newProduct.classification_id" :invalid="newProductSubmitted && !newProduct.category_id" optionLabel="name" optionValue="id" fluid required />
                  </div>
                  <div class="md:col-span-2">
                    <label for="new-storage" class="mb-1 block text-sm font-medium text-slate-700">Склад</label>
                    <Select id="new-storage" v-model="newProduct.storage_id" :options="storages" :invalid="newProductSubmitted && !newProduct.storage_id" optionLabel="name" optionValue="id" fluid required />
                  </div>
                </div>
              </section>

              <section class="rounded-2xl border border-slate-200 bg-white p-3.5">
                <div class="mb-3">
                  <h3 class="text-sm font-semibold text-slate-900">Фото</h3>
                  <p class="mt-1 text-xs text-slate-500">Добавьте изображение товара при необходимости.</p>
                </div>

                <div class="flex flex-col items-center gap-2.5">
                  <FileUpload
                    class="w-full border-2 border-dashed border-slate-200 bg-slate-50"
                    severity="info"
                    mode="basic"
                    accept="image/*"
                    auto
                    fluid
                    customUpload
                    chooseLabel="Добавить фото"
                    :chooseButtonProps="{ severity: 'secondary' }"
                    @select="onSelect"
                  />
                  <div v-if="preview" class="relative mt-1 inline-block">
                    <img :src="preview" width="136" class="rounded-2xl border border-slate-200 bg-slate-50 object-cover" />
                    <Button
                      icon="pi pi-times"
                      severity="danger"
                      rounded
                      size="small"
                      class="!absolute !right-2 !top-2 !h-8 !w-8 !min-w-8"
                      @click="clearNewProductImage()"
                    />
                  </div>
                </div>
              </section>
            </div>
            <template #footer>
              <div class="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button label="Отменить" icon="pi pi-times" @click="newProductDialog = false, newProductSubmitted = false" severity="secondary" outlined class="sm:min-w-32" />
                <Button label="Добавить" icon="pi pi-check" @click="createProduct()" severity="success" class="sm:min-w-32" />
              </div>
            </template>
          </Dialog>
        
          <Dialog
            v-model:visible="editedProductDialog"
            :style="{ width: '680px', maxWidth: '96vw' }"
            header="Редактировать товар"
            :modal="true"
            class="system-form-dialog"
          >
            <div class="max-h-[72vh] space-y-3 overflow-y-auto pr-1">
              <section class="rounded-2xl border border-slate-200 bg-slate-50 p-3.5">
                <div class="mb-3">
                  <h3 class="text-sm font-semibold text-slate-900">Основное</h3>
                  <p class="mt-1 text-xs text-slate-500">Обновите название, штрихкоды и описание товара.</p>
                </div>

                <div class="grid gap-3 md:grid-cols-2">
                  <div class="md:col-span-2">
                    <label for="edit-name" class="mb-1 block text-sm font-medium text-slate-700">Название</label>
                    <InputText id="edit-name" v-model.trim="editedProduct.name" fluid required />
                  </div>
                  <div class="md:col-span-2">
                    <label for="edit-name-ru" class="mb-1 block text-sm font-medium text-slate-700">Название RU</label>
                    <InputText id="edit-name-ru" v-model.trim="editedProduct.name_ru" fluid />
                  </div>
                  <div class="md:col-span-2">
                    <label for="edit-barcode" class="mb-1 block text-sm font-medium text-slate-700">Штрихкод</label>
                    <Chips
                      id="edit-barcode"
                      class="w-full"
                      v-model="editedProduct.barcode"
                      @add="onBarcodeChipsAdd"
                      :pt="{
                        input: { inputmode: 'numeric', pattern: '[0-9]*' }
                      }"
                    />
                  </div>
                  <div class="md:col-span-2">
                    <label for="edit-description" class="mb-1 block text-sm font-medium text-slate-700">Описание</label>
                    <InputText id="edit-description" v-model.trim="editedProduct.description" fluid />
                  </div>
                </div>
              </section>

              <section class="rounded-2xl border border-slate-200 bg-white p-3.5">
                <div class="mb-3">
                  <h3 class="text-sm font-semibold text-slate-900">Классификация</h3>
                  <p class="mt-1 text-xs text-slate-500">Выберите актуальный класс и категорию товара.</p>
                </div>

                <div class="grid gap-3 md:grid-cols-2">
                  <div>
                    <label for="edit-classification" class="mb-1 block text-sm font-medium text-slate-700">Класс</label>
                    <Select id="edit-classification" v-model="editedProduct.classification_id" @change="onChangeClassification($event)" :options="classifications" :invalid="editedProductSubmitted && !editedProduct.classification_id" optionLabel="name" optionValue="id" fluid required />
                  </div>
                  <div>
                    <label for="edit-category" class="mb-1 block text-sm font-medium text-slate-700">Категория</label>
                    <Select id="edit-category" v-model="editedProduct.category_id" :options="categories" :disabled="!editedProduct.classification_id" :invalid="editedProductSubmitted && !editedProduct.category_id" optionLabel="name" optionValue="id" fluid required />
                  </div>
                </div>
              </section>

              <section class="rounded-2xl border border-slate-200 bg-white p-3.5">
                <div class="mb-3">
                  <h3 class="text-sm font-semibold text-slate-900">Фото</h3>
                  <p class="mt-1 text-xs text-slate-500">Загрузите новое изображение или очистите текущее.</p>
                </div>

                <div class="flex flex-col items-center gap-2.5">
                  <FileUpload
                    class="w-full border-2 border-dashed border-slate-200 bg-slate-50"
                    severity="info"
                    mode="basic"
                    accept="image/*"
                    auto
                    fluid
                    customUpload
                    chooseLabel="Заменить фото"
                    :chooseButtonProps="{ severity: 'secondary' }"
                    @select="onEditSelect"
                  />
                  <div v-if="editedPreview || editedProduct.image" class="relative mt-1 inline-block">
                    <img :src="editedPreview || resolveProductImage(editedProduct.image)" width="136" class="rounded-2xl border border-slate-200 bg-slate-50 object-cover" />
                    <Button
                      icon="pi pi-times"
                      severity="danger"
                      rounded
                      size="small"
                      class="!absolute !right-2 !top-2 !h-8 !w-8 !min-w-8"
                      @click="clearEditedProductImage()"
                    />
                  </div>
                </div>
              </section>
            </div>
            <template #footer>
              <div class="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button label="Отменить" icon="pi pi-times" @click="editedProductDialog = false, editedProductSubmitted = false" severity="secondary" outlined class="sm:min-w-32" />
                <Button label="Сохранить" icon="pi pi-check" @click="updateProduct()" severity="success" class="sm:min-w-32" />
              </div>
            </template>
          </Dialog>

          <Dialog v-model:visible="imageDialog" :style="{ width: '500px' }" :header="selectedProduct.name" :modal="true" v-if="selectedProduct">
            <div class="flex justify-center items-center gap-4">
              <div class="w-full">
                <img :src="resolveProductImage(selectedProduct.image)" :alt="selectedProduct.name" class="w-full h-full object-cover"/>
              </div>
            </div>
          </Dialog>

          <Dialog v-model:visible="importDialog" :style="{ width: '560px' }" header="Импорт товаров из Excel" :modal="true">
            <div class="flex flex-col gap-4">
              <div class="text-sm text-gray-600">
                Поддерживается файл с колонками:
                <b>name</b>, <b>name_ru</b>, <b>storage</b>, <b>classification</b>, <b>category</b>, <b>description</b>,
                <b>counterparty</b>, <b>barcode</b>, <b>date</b>, <b>unit</b>, <b>amount</b>, <b>price</b>, <b>store_price_by_day</b>.
              </div>

              <div class="flex flex-col gap-2">
                <FileUpload
                  mode="basic"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  customUpload
                  chooseLabel="Выбрать Excel"
                  :chooseButtonProps="{ severity: 'secondary' }"
                  @select="onImportFileSelect"
                />
                <div v-if="importFile" class="text-sm text-gray-600">
                  Файл: {{ importFile.name }}
                </div>
              </div>
            </div>

            <template #footer>
              <div class="flex justify-evenly w-full gap-2">
                <Button label="Отменить" icon="pi pi-times" @click="closeImportDialog()" severity="danger" fluid />
                <Button :label="importLoading ? 'Импорт...' : 'Загрузить'" :loading="importLoading" icon="pi pi-check" @click="importProductsFromExcel()" severity="success" fluid />
              </div>
            </template>
          </Dialog>

          <Dialog
            v-model:visible="barcodeBatchDialog"
            :style="{ width: '960px', maxWidth: '96vw' }"
            header="Выгрузка PDF штрихкодов"
            :modal="true"
            class="system-form-dialog"
          >
            <div class="space-y-4">
              <section class="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <h3 class="text-sm font-semibold text-slate-900">Генерация 100 новых штрихкодов</h3>
                    <p class="mt-1 text-xs text-slate-500">
                      Система создаст 100 новых уникальных штрихкодов для печати и клейки на товары,
                      зарезервирует их в базе и сохранит PDF в истории выгрузок.
                    </p>
                  </div>
                  <Button
                    label="Выгрузить 100 штрихкодов"
                    icon="pi pi-barcode"
                    severity="success"
                    :loading="barcodeBatchGenerating"
                    @click="generateLabelPoolBatch()"
                  />
                </div>
              </section>

              <section class="rounded-2xl border border-slate-200 bg-white p-4">
                <div class="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <h3 class="text-sm font-semibold text-slate-900">История выгрузок</h3>
                    <p class="mt-1 text-xs text-slate-500">Сохраненные PDF и последние сгенерированные пакеты штрихкодов.</p>
                  </div>
                  <Button
                    icon="pi pi-refresh"
                    label="Обновить"
                    severity="secondary"
                    outlined
                    size="small"
                    :loading="barcodeBatchHistoryLoading"
                    @click="fetchBarcodeBatchHistory()"
                  />
                </div>

                <div v-if="barcodeBatchHistoryLoading" class="py-8 text-center text-sm text-slate-500">
                  Загрузка истории...
                </div>

                <div v-else-if="!barcodeBatchHistory.length" class="py-8 text-center text-sm text-slate-500">
                  История выгрузок пока пуста
                </div>

                <div v-else class="max-h-[52vh] overflow-auto rounded-2xl border border-slate-200">
                  <table class="min-w-full divide-y divide-slate-200 text-sm">
                    <thead class="bg-slate-50">
                      <tr>
                        <th class="px-4 py-3 text-left font-semibold text-slate-600">Дата</th>
                        <th class="px-4 py-3 text-center font-semibold text-slate-600">Всего</th>
                        <th class="px-4 py-3 text-center font-semibold text-slate-600">Товаров</th>
                        <th class="px-4 py-3 text-left font-semibold text-slate-600">Файл</th>
                        <th class="px-4 py-3 text-left font-semibold text-slate-600">Превью</th>
                        <th class="px-4 py-3 text-right font-semibold text-slate-600">PDF</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-100 bg-white">
                      <tr v-for="batch in barcodeBatchHistory" :key="batch.id" class="align-top">
                        <td class="px-4 py-3 text-slate-700">{{ formatBatchDate(batch.created_at) }}</td>
                        <td class="px-4 py-3 text-center font-semibold text-slate-900">{{ batch.total_count }}</td>
                        <td class="px-4 py-3 text-center text-slate-700">{{ batch.product_count }}</td>
                        <td class="px-4 py-3 text-slate-700">{{ batch.pdf_file_name || '—' }}</td>
                        <td class="px-4 py-3">
                          <div class="flex max-w-md flex-wrap gap-1.5">
                            <span
                              v-for="item in batch.preview_items || []"
                              :key="`${batch.id}-${item.entity_type}-${item.entity_id}`"
                              class="rounded-full bg-slate-100 px-2 py-1 text-[11px] text-slate-700"
                            >
                              {{ item.entity_name || 'Штрихкод' }} · {{ item.barcode }}
                            </span>
                          </div>
                        </td>
                        <td class="px-4 py-3 text-right">
                          <Button
                            icon="pi pi-download"
                            label="Скачать"
                            size="small"
                            severity="secondary"
                            :disabled="!batch.pdf_url"
                            @click="downloadBatchPdf(batch)"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import axios from '../lib/axios'
import * as XLSX from 'xlsx'
import { FilterMatchMode, FilterOperator } from '@primevue/core/api'
import { useConfirm } from "primevue/useconfirm";
import { useToast } from 'primevue/usetoast'
import Toast from 'primevue/toast'
import { blobToBase64, createBarcodeBatchPdfBlob, downloadPdfBlob, exportBarcodePdfLabel } from '../utils/barcodePdf'

const toast = useToast()
const confirm = useConfirm();
const filters = ref([])
const products = ref([])
const productTable = ref()
const classifications = ref([])
const categories = ref([])
const categoryOptions = ref([])
const storages = ref([])
const barcode = ref('')
const selectedStorageFilter = ref(null)
const selectedClassificationFilter = ref(null)
const selectedCategoryFilter = ref(null)

const newProduct = ref({})
const newProductDialog = ref(false)
const newProductSubmitted = ref(false)

const editedProduct = ref({})
const editedProductDialog = ref(false)
const editedProductSubmitted = ref(false)

const imageDialog = ref(false)
const selectedProduct = ref()
const selectedFile = ref(null);
const preview = ref(null);
const editedFile = ref(null);
const editedPreview = ref(null);
const importDialog = ref(false)
const importFile = ref(null)
const importSubmitted = ref(false)
const importLoading = ref(false)
const barcodeGenerating = ref(false)
const barcodeBatchDialog = ref(false)
const barcodeBatchGenerating = ref(false)
const barcodeBatchHistoryLoading = ref(false)
const barcodeBatchHistory = ref([])

const defaultProductImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 160 160'%3E%3Crect width='160' height='160' rx='24' fill='%23f8fafc'/%3E%3Crect x='20' y='20' width='120' height='120' rx='20' fill='%23e2e8f0'/%3E%3Cpath d='M52 104l18-22 16 18 12-14 18 18H52z' fill='%2394a3b8'/%3E%3Ccircle cx='64' cy='62' r='10' fill='%23cbd5e1'/%3E%3C/svg%3E"

// filters
const initFilters = () => {
    filters.value = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        name: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
    };
};
initFilters();

const clearFilter = () => {
  initFilters()
  selectedStorageFilter.value = null
  selectedClassificationFilter.value = null
  selectedCategoryFilter.value = null
}

const filteredProducts = computed(() => {
  const query = String(filters.value?.global?.value || '').trim().toLowerCase()

  const scopedProducts = products.value.filter((product) => {
    const storageMatch = !selectedStorageFilter.value || product.storage === selectedStorageFilter.value
    const classificationMatch = !selectedClassificationFilter.value || product.classification === selectedClassificationFilter.value
    const categoryMatch = !selectedCategoryFilter.value || product.category === selectedCategoryFilter.value

    return storageMatch && classificationMatch && categoryMatch
  })

  if (!query) {
    return scopedProducts
  }

  return scopedProducts.filter((product) => {
    const barcodeText = Array.isArray(product.barcode)
      ? product.barcode.join(' ')
      : String(product.barcode || '')

    return [
      product.name,
      product.name_ru,
      barcodeText,
      product.storage,
      product.classification,
      product.category,
      product.description,
    ].some((value) => String(value || '').toLowerCase().includes(query))
  })
})

const filteredProductsCount = computed(() => filteredProducts.value.length)

const filteredCategoryOptions = computed(() => {
  if (!selectedClassificationFilter.value) {
    return categoryOptions.value
  }

  const selectedClassification = classifications.value.find(
    (item) => item.name === selectedClassificationFilter.value
  )

  if (!selectedClassification?.id) {
    return []
  }

  return categoryOptions.value.filter(
    (item) => item.classification_id === selectedClassification.id
  )
})

watch(selectedClassificationFilter, (nextValue, previousValue) => {
  if (nextValue !== previousValue) {
    selectedCategoryFilter.value = null
  }
})

const resolveProductImage = (image) => {
  return image || defaultProductImage
}

const normalizeSingleBarcode = (value) => String(value ?? '').replace(/\D+/g, '').trim()

const getPrimaryBarcode = (product) => {
  const codes = Array.isArray(product?.barcode) ? product.barcode : []
  const firstCode = codes.find((code) => String(code || '').trim())
  return firstCode ? String(firstCode).trim() : ''
}

const onBarcodeChipsAdd = (event) => {
  const value = event.value
  if (editedProduct.value?.barcode?.filter((code) => code === value).length > 1) {
    editedProduct.value.barcode.pop()
  }
}

const generateNewProductBarcode = async () => {
  barcodeGenerating.value = true

  try {
    const { data } = await axios.post('/api/products/barcode')
    const nextBarcode = String(data?.barcode || '').trim()

    if (!nextBarcode) {
      throw new Error('Не удалось сгенерировать штрихкод')
    }

    barcode.value = nextBarcode
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      'Не удалось сгенерировать штрихкод'
    toast.add({ severity: 'error', summary: 'Ошибка', detail: errorMessage, life: 5000 })
  } finally {
    barcodeGenerating.value = false
  }
}

const exportProductBarcodePdf = async (product) => {
  const productBarcode = getPrimaryBarcode(product)

  if (!productBarcode) {
    toast.add({ severity: 'warn', summary: 'Штрихкод не найден', detail: 'У товара нет штрихкода для экспорта', life: 4000 })
    return
  }

  try {
    await exportBarcodePdfLabel({
      heading: 'Штрих-код товара',
      title: product?.name || 'Товар',
      barcode: productBarcode,
      fileName: `Штрихкод товара-${product?.name || product?.id || 'export'}.pdf`
    })
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось экспортировать PDF штрих-кода', life: 4000 })
    console.error(error)
  }
}

const onSelect = (e) => {
  selectedFile.value = e.files[0];
  preview.value = URL.createObjectURL(selectedFile.value);
};
const onEditSelect = (e) => {
  editedFile.value = e.files[0];
  editedPreview.value = URL.createObjectURL(editedFile.value);
}

const clearNewProductImage = () => {
  selectedFile.value = null
  preview.value = null
}

const clearEditedProductImage = () => {
  editedFile.value = null
  editedPreview.value = null
  editedProduct.value.image = null
}

const openImage = (product) => {
  if (product) {
    imageDialog.value = true
    selectedProduct.value = product
  }
}

const openImportDialog = () => {
  importDialog.value = true
}

const openBarcodeBatchDialog = async () => {
  barcodeBatchDialog.value = true
  await fetchBarcodeBatchHistory()
}

const closeImportDialog = () => {
  importDialog.value = false
  importFile.value = null
  importSubmitted.value = false
  importLoading.value = false
}

const formatBatchDate = (value) => {
  if (!value) return '—'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)

  return date.toLocaleString('ru-RU')
}

const triggerFileDownload = (url, fallbackName) => {
  const link = document.createElement('a')
  link.href = url
  link.download = fallbackName
  link.target = '_blank'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const downloadBatchPdf = (batch) => {
  if (!batch?.pdf_url) return

  triggerFileDownload(batch.pdf_url, `barcode-batch-${batch.id}.pdf`)
}

const uploadBatchPdf = async (batchId, pdfBlob) => {
  const pdfBase64 = await blobToBase64(pdfBlob)
  const { data } = await axios.post(`/api/barcode-export-batches/${batchId}/pdf`, {
    file_name: `barcode-batch-${batchId}.pdf`,
    pdf_base64: pdfBase64,
  })

  return data?.batch || null
}

const fetchBarcodeBatchHistory = async () => {
  barcodeBatchHistoryLoading.value = true

  try {
    const { data } = await axios.get('/api/barcode-export-batches')
    barcodeBatchHistory.value = Array.isArray(data) ? data : []
  } catch (error) {
    const message = error?.response?.data?.message || 'Не удалось загрузить историю выгрузок'
    toast.add({ severity: 'error', summary: 'Ошибка', detail: message, life: 5000 })
  } finally {
    barcodeBatchHistoryLoading.value = false
  }
}

const generateLabelPoolBatch = async () => {
  barcodeBatchGenerating.value = true

  try {
    const { data } = await axios.post('/api/barcode-export-batches/generate-label-pool', {
      limit: 100,
    })

    const batch = data?.batch || null
    const items = Array.isArray(data?.items) ? data.items : []
    if (!batch) {
      toast.add({ severity: 'warn', summary: 'Ошибка', detail: data?.message || 'Не удалось сформировать PDF штрихкодов', life: 5000 })
      await fetchBarcodeBatchHistory()
      return
    }

    if (!items.length) {
      toast.add({ severity: 'warn', summary: 'Ошибка', detail: 'Список штрихкодов для PDF пуст', life: 5000 })
      await fetchBarcodeBatchHistory()
      return
    }

    const pdfBlob = await createBarcodeBatchPdfBlob({ items })
    const savedBatch = await uploadBatchPdf(batch.id, pdfBlob)
    const fileName = savedBatch?.pdf_file_name || `barcode-batch-${batch.id}.pdf`

    await fetchProducts()
    await fetchBarcodeBatchHistory()
    downloadPdfBlob(pdfBlob, fileName)

    toast.add({
      severity: 'success',
      summary: 'PDF сформирован',
      detail: `Создано ${batch.total_count} новых уникальных штрихкодов`,
      life: 6000,
    })
  } catch (error) {
    const validationMessage = error?.response?.data?.errors
      ? Object.values(error.response.data.errors).flat()[0]
      : null
    const message = validationMessage || error?.message || error?.response?.data?.message || 'Не удалось сформировать пакет штрихкодов'
    toast.add({ severity: 'error', summary: 'Ошибка', detail: message, life: 6000 })
  } finally {
    barcodeBatchGenerating.value = false
  }
}

const onImportFileSelect = (e) => {
  importFile.value = e.files?.[0] ?? null
}

const normalizeCellValue = (value) => String(value ?? '').trim()

const normalizeHeader = (value) => normalizeCellValue(value).toLowerCase().replace(/\s+/g, ' ')

const readExcelRows = async (file) => {
  const buffer = await file.arrayBuffer()
  const workbook = XLSX.read(buffer, { type: 'array' })
  const firstSheetName = workbook.SheetNames[0]

  if (!firstSheetName) {
    throw new Error('Файл Excel не содержит листов')
  }

  return XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], {
    header: 1,
    defval: '',
    raw: false,
  })
}

const getColumnIndexes = (headers) => {
  const normalizedHeaders = headers.map(normalizeHeader)

  const findColumn = (variants) => normalizedHeaders.findIndex(header => variants.includes(header))

  const indexes = {
    name: findColumn(['name']),
    name_ru: findColumn(['name_ru']),
    storage: findColumn(['storage']),
    classification: findColumn(['classification']),
    category: findColumn(['category']),
    description: findColumn(['description']),
    counterparty: findColumn(['counterparty']),
    barcode: findColumn(['barcode', 'штрих код', 'штрихкод']),
    date: findColumn(['date']),
    unit: findColumn(['unit']),
    amount: findColumn(['amount']),
    price: findColumn(['price']),
    store_price_by_day: findColumn(['store_price_by_day']),
  }

  const missingColumns = Object.entries(indexes)
    .filter(([, index]) => index === -1)
    .map(([key]) => key)

  if (missingColumns.length) {
    throw new Error(`В Excel не найдены обязательные колонки: ${missingColumns.join(', ')}`)
  }

  return indexes
}

const buildImportPayload = (rows) => {
  if (!rows.length) {
    throw new Error('Файл Excel пустой')
  }

  const columnIndexes = getColumnIndexes(rows[0])
  const payload = []

  rows.slice(1).forEach((row, index) => {
    const payloadRow = {
      name: normalizeCellValue(row[columnIndexes.name]),
      name_ru: normalizeCellValue(row[columnIndexes.name_ru]),
      storage: normalizeCellValue(row[columnIndexes.storage]),
      classification: normalizeCellValue(row[columnIndexes.classification]),
      category: normalizeCellValue(row[columnIndexes.category]),
      description: normalizeCellValue(row[columnIndexes.description]),
      counterparty: normalizeCellValue(row[columnIndexes.counterparty]),
      barcode: normalizeCellValue(row[columnIndexes.barcode]),
      date: normalizeCellValue(row[columnIndexes.date]),
      unit: normalizeCellValue(row[columnIndexes.unit]),
      amount: normalizeCellValue(row[columnIndexes.amount]),
      price: normalizeCellValue(row[columnIndexes.price]),
      store_price_by_day: normalizeCellValue(row[columnIndexes.store_price_by_day]),
    }
    const isEmptyRow = Object.values(payloadRow).every(value => !value)
    const hasProductIdentity = Boolean(
      payloadRow.name ||
      payloadRow.name_ru ||
      payloadRow.barcode ||
      payloadRow.counterparty
    )

    if (isEmptyRow || !hasProductIdentity) {
      return
    }

    payload.push({
      rowNumber: index + 2,
      ...payloadRow,
    })
  })

  if (!payload.length) {
    throw new Error('В Excel нет строк для импорта')
  }

  return payload
}

const importProductsFromExcel = async () => {
  importSubmitted.value = true

  if (!importFile.value) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Выберите Excel-файл', life: 5000 })
    return
  }

  importLoading.value = true

  try {
    const rows = await readExcelRows(importFile.value)
    const parsedRows = buildImportPayload(rows)
    const { data } = await axios.post('/api/products/import', { rows: parsedRows })
    await fetchProducts()

    const problems = Array.isArray(data?.errors) ? data.errors : []
    const imported = Number(data?.imported || 0)
    const summary = [`Импортировано: ${imported}`]

    if (problems.length) {
      summary.push(`Ошибок/пропусков: ${problems.length}`)
      toast.add({
        severity: imported > 0 ? 'warn' : 'error',
        summary: imported > 0 ? 'Импорт завершён частично' : 'Импорт не выполнен',
        detail: summary.join('. '),
        life: 8000,
      })
      console.warn('Ошибки импорта товаров:', problems)
    } else {
      toast.add({
        severity: 'success',
        summary: 'Успешно!',
        detail: summary[0],
        life: 5000,
      })
    }

    if (problems.length > 0) {
      console.table(problems.map(message => ({ message })))
    }

    closeImportDialog()
  } catch (error) {
    const responseErrors = Array.isArray(error?.response?.data?.errors) ? error.response.data.errors : []
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.errors?.rows?.[0] ||
      responseErrors[0] ||
      error?.message ||
      'Не удалось импортировать Excel'
    toast.add({ severity: 'error', summary: 'Ошибка', detail: message, life: 7000 })
    console.error(error)
  } finally {
    importLoading.value = false
  }
}

// create product
const createProduct = async () => {
  newProductSubmitted.value = true

  const hasRequiredFields =
    newProduct.value.name &&
    newProduct.value.classification_id &&
    newProduct.value.category_id &&
    newProduct.value.storage_id &&
    normalizeSingleBarcode(barcode.value).length > 0

  if (!hasRequiredFields) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Поля не заполнены', life: 5000 })
    return
  }

  const formData = new FormData()
  formData.append('name', newProduct.value.name)
  formData.append('name_ru', newProduct.value.name_ru || '')
  formData.append('classification_id', String(newProduct.value.classification_id))
  formData.append('category_id', String(newProduct.value.category_id))
  formData.append('storage_id', String(newProduct.value.storage_id))
  formData.append('description', newProduct.value.description || '')

  formData.append('barcode[0]', normalizeSingleBarcode(barcode.value))

  if (selectedFile.value instanceof File) {
    formData.append('image', selectedFile.value)
  }

  try {
    await axios.post('/api/products', formData)
    toast.add({ severity: 'success', summary: 'Успешно!', detail: 'Товар добавлен', life: 5000 })
    await fetchProducts()

    newProduct.value = {}
    barcode.value = ''
    selectedFile.value = null
    preview.value = null
    newProductSubmitted.value = false
    newProductDialog.value = false
  } catch (e) {
    const errorMessage =
      e?.response?.data?.errors?.image?.[0] ||
      e?.response?.data?.message ||
      'Не удалось сохранить товар'
    toast.add({ severity: 'error', summary: 'Ошибка', detail: errorMessage, life: 5000 })
    console.error(e)
  }
}

const editProduct = async (id) => {
  editedProduct.value = products.value.find(p => p.id == id)
  editedProductDialog.value = true
  getCategories()
}

const updateProduct = async () => {
  if(!editedProduct.value.classification_id || !editedProduct.value.category_id) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Поля не заполнены',   life: 5000})
  } else {
    try {
      editedProduct.value.image = editedFile.value instanceof File ? editedFile.value : null;
      const res = await axios.post(`/api/products/${editedProduct.value.id}?_method=PUT`, editedProduct.value, {
        headers: { "Content-Type": "multipart/form-data"}
      })
      products.value.push(res.data)

      toast.add({ severity:'success', summary: 'Успешно!', detail: 'Товар отредактирован', life: 5000 })
      
      fetchProducts()
      editedProduct.value = {}
      editedProductSubmitted.value = false
      editedProductDialog.value = false
    } catch (e) {
      toast.add({ severity:'error', summary: 'Ошибка', detail: 'Не удалось сохранить товар', life: 5000 })
      console.error(e)
    }
  }
}
const deleteProduct = async (id) => {
    confirm.require({
        message: 'Вы уверены что хотите удалить товар?',
        header: 'Удалить выбранный товар',
        icon: 'pi pi-info-circle',
        rejectLabel: 'Отменить',
        rejectProps: {
            label: 'Отменить',
            severity: 'secondary',
            outlined: true
        },
        acceptProps: {
            label: 'Удалить',
            severity: 'danger'
        },
        accept: async () => {
          try {
            const res = await axios.delete(`/api/products/${id}`)
            const isHttpSuccess = res.status >= 200 && res.status < 300
            const hasErrorFlag = res?.data?.success === false || Boolean(res?.data?.error)

            if (!isHttpSuccess || hasErrorFlag) {
              throw new Error(res?.data?.message || 'Не удалось удалить товар')
            }

            toast.add({
              severity: 'success',
              summary: 'Успешно',
              detail: res?.data?.message || 'Товар удален',
              life: 3000
            });
            await fetchProducts()
          } catch (e) {
            const rawMessage = e?.response?.data?.message || e?.message || 'Не удалось удалить товар'
            const cleanedMessage = typeof rawMessage === 'string'
              ? rawMessage.split('SQLSTATE')[0].trim()
              : 'Не удалось удалить товар'
            const errorMessage = cleanedMessage || 'Не удалось удалить товар'
            toast.add({ severity: 'error', summary: 'Ошибка', detail: errorMessage, life: 5000 })
            console.error(e)
          }
        },
        reject: () => {
            toast.add({ severity: 'info', summary: 'Отмена', detail: 'Отмена удаления', life: 3000 });
        }
    });
};

const onChangeClassification = async (e) => {
  editedProduct.value.category_id = null
  newProduct.value.category_id = null
  const res = await axios.get('/api/category-by-class?classification=' + e.value )
  categories.value = res.data
}

// get data
const fetchProducts = async () => {
  const res = await axios.get(`/api/products`)
  products.value = res.data
}

const getClassifications = async () => {
  const res = await axios.get('/api/classifications')
  classifications.value = res.data
}

const getCategories = async () => {
  if (editedProduct.value.classification_id) {
    const res = await axios.get('/api/category')
    categories.value = Object.values(res.data.filter(p => p.classification_id == editedProduct.value.classification_id))
  }
}

const getCategoryOptions = async () => {
  const res = await axios.get('/api/category')
  categoryOptions.value = res.data
}

const getStorages = async () => {
    const res = await axios.get('/api/get-storage') 
    storages.value = res.data;
}

const exportCSV = () => {
  productTable.value.exportCSV();
};

onMounted(() => {
  fetchProducts()
  getClassifications()
  getCategoryOptions()
  getStorages()
  fetchBarcodeBatchHistory()
})
</script>

<style scoped>
:deep(.system-form-dialog .p-dialog-header) {
  padding: 1rem 1.25rem 0.7rem;
  border-bottom: 1px solid rgb(226 232 240);
}

:deep(.system-form-dialog .p-dialog-content) {
  padding: 1rem 1.25rem;
  background: rgb(255 255 255);
}

:deep(.system-form-dialog .p-dialog-footer) {
  padding: 0.85rem 1.25rem 1rem;
  border-top: 1px solid rgb(226 232 240);
}

:deep(.product-table .p-datatable-table-container) {
  height: 100%;
  border-radius: 1rem;
  border: 1px solid rgb(226 232 240);
}

:deep(.product-table .p-datatable-header) {
  border: 0;
  background: white;
  padding: 0;
}

:deep(.product-table .p-datatable-thead > tr > th) {
  position: sticky;
  top: 0;
  z-index: 5;
  background: rgb(248 250 252);
  color: rgb(71 85 105);
  font-weight: 600;
  border-color: rgb(226 232 240);
  padding: 0.9rem 1rem;
}

:deep(.product-table .p-datatable-tbody > tr > td) {
  border-color: rgb(241 245 249);
  color: rgb(30 41 59);
  padding: 0.9rem 1rem;
  vertical-align: middle;
}

:deep(.product-table .p-datatable-tbody > tr:hover) {
  background: rgba(240, 253, 250, 0.7);
}
</style>
